import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Sun, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSettings } from "@/contexts/SettingsContext";
import { useTheme } from "@/contexts/ThemeContext";

const navLinks = [
  { href: "/", label: "الرئيسية" },
  { href: "/about", label: "من نحن" },
  { href: "/marketing", label: "التسويق" },
  { href: "/production", label: "الإنتاج" },
  { href: "/training", label: "التدريب والتوظيف" },
  { href: "/portfolio", label: "أعمالنا" },
  { href: "/influencers", label: "صناع المحتوى" },
  { href: "/courses", label: "الدورات" },
  { href: "/contact", label: "تواصل معنا" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const settings = useSettings();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const logoUrl = theme === "dark"
    ? (settings.logo_dark_url || settings.logo_url)
    : (settings.logo_light_url || settings.logo_url);

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? theme === "dark"
            ? "bg-[#111112]/95 backdrop-blur-lg border-b border-white/5 shadow-lg"
            : "bg-white/95 backdrop-blur-lg border-b border-black/5 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            {logoUrl ? (
              <img src={logoUrl} alt={settings.site_name} className="h-8 md:h-10 object-contain" />
            ) : (
              <span className={`text-2xl md:text-3xl font-bold font-['Zain'] ${theme === "dark" ? "text-white" : scrolled ? "text-gray-900" : "text-white"}`}>
                {settings.site_name}
              </span>
            )}
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse-glow"></span>
          </Link>

          {/* Desktop Nav + Theme Toggle */}
          <div className="hidden lg:flex items-center gap-1">
            <nav className="flex items-center gap-1" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-3 py-2 text-sm font-medium transition-colors relative
                    after:content-[''] after:absolute after:bottom-0 after:right-0 after:w-0 after:h-[2px] after:bg-primary after:transition-all hover:after:w-full
                    ${
                      location.pathname === link.href
                        ? "text-primary after:w-full"
                        : theme === "dark"
                          ? "text-zinc-400 hover:text-white"
                          : scrolled
                            ? "text-gray-500 hover:text-gray-900"
                            : "text-zinc-300 hover:text-white"
                    }`}
                  data-testid={`nav-link-${link.href.replace("/", "") || "home"}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`ms-3 w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-300 ${
                theme === "dark"
                  ? "bg-white/10 text-zinc-300 hover:bg-white/20 hover:text-white"
                  : scrolled
                    ? "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                    : "bg-white/10 text-zinc-300 hover:bg-white/20 hover:text-white"
              }`}
              data-testid="theme-toggle-btn"
              title={theme === "dark" ? "الوضع النهاري" : "الوضع الليلي"}
            >
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>

          {/* Mobile: Theme Toggle + Menu */}
          <div className="flex items-center gap-2 lg:hidden">
            <button
              onClick={toggleTheme}
              className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${
                theme === "dark"
                  ? "bg-white/10 text-zinc-300"
                  : "bg-black/5 text-gray-600"
              }`}
              data-testid="theme-toggle-mobile"
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <Button
              variant="ghost"
              size="icon"
              className={`${theme === "dark" ? "text-white" : scrolled ? "text-gray-900" : "text-white"}`}
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div
          className={`lg:hidden backdrop-blur-xl border-t ${
            theme === "dark"
              ? "bg-[#111112]/98 border-white/5"
              : "bg-white/98 border-black/5"
          }`}
          data-testid="mobile-menu"
        >
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === link.href
                    ? "bg-primary/10 text-primary"
                    : theme === "dark"
                      ? "text-zinc-400 hover:text-white hover:bg-white/5"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                }`}
                data-testid={`mobile-nav-${link.href.replace("/", "") || "home"}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
